// ─────────────────────────────────────────────────────────────────────────────
// SAP Insights POC — Filter Context
//
// Fully separate instance from AutomationFilterContext.tsx (own Context, own
// Provider, own default) — same shape/logic, just a 2-week default instead of
// 12 weeks, per the user's explicit request for this POC page. Scoped locally
// to the SAP Insights page tree, same as AutomationFilterProvider is scoped
// to Automation Insights.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState } from 'react';
import { format, subWeeks } from 'date-fns';

export type SapFilterPreset = '2w' | '4w' | '8w' | '12w' | 'custom';
export type TimeFilterType = 'day' | 'week' | 'month';

export interface SapFilterState {
  timeFilterType: TimeFilterType;
  selectedPeriods: string[];
  preset: SapFilterPreset;
  /** ISO date string YYYY-MM-DD */
  dateFrom: string;
  /** ISO date string YYYY-MM-DD */
  dateTo: string;
  label: string;
}

export interface SapFilterContextValue {
  filters: SapFilterState;
  setFilters: (filters: SapFilterState) => void;
}

export interface SapTimeFilterRequestParams {
  timeFilterType: TimeFilterType;
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export const SAP_PRESET_CONFIG: Record<Exclude<SapFilterPreset, 'custom'>, { weeks: number; label: string }> = {
  '2w': { weeks: 2, label: 'last 2 weeks' },
  '4w': { weeks: 4, label: 'last 4 weeks' },
  '8w': { weeks: 8, label: 'last 8 weeks' },
  '12w': { weeks: 12, label: 'last 12 weeks' },
};

export function buildSapPresetFilter(preset: Exclude<SapFilterPreset, 'custom'>): SapFilterState {
  const today = new Date();
  const { weeks, label } = SAP_PRESET_CONFIG[preset];
  return {
    timeFilterType: 'day',
    selectedPeriods: [],
    preset,
    dateFrom: format(subWeeks(today, weeks), 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd'),
    label,
  };
}

// Default state — 2 weeks, per the user's explicit request for this POC page
// (vs. 12 weeks on the automation page).
export const defaultSapFilters: SapFilterState = buildSapPresetFilter('2w');

export function buildSapTimeFilterRequestParams(filters: SapFilterState): SapTimeFilterRequestParams {
  if (filters.timeFilterType === 'day') {
    return {
      timeFilterType: 'day',
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    };
  }

  return {
    timeFilterType: filters.timeFilterType,
    selectedPeriods: filters.selectedPeriods,
  };
}

export function validateSapFilterSelection(filters: SapFilterState): string | null {
  if (filters.timeFilterType === 'day') {
    if (!filters.dateFrom || !filters.dateTo) {
      return 'Select both from and to dates.';
    }
    return null;
  }

  if (!filters.selectedPeriods || filters.selectedPeriods.length === 0) {
    return filters.timeFilterType === 'week'
      ? 'Select at least one week.'
      : 'Select at least one month.';
  }

  return null;
}

export const SapFilterContext = createContext<SapFilterContextValue>({
  filters: defaultSapFilters,
  setFilters: () => {},
});

export const useSapFilters = (): SapFilterContextValue => useContext(SapFilterContext);

export function SapFilterProvider({ children }: { children: React.ReactNode }) {
  const [filters, setFilters] = useState<SapFilterState>(defaultSapFilters);

  return (
    <SapFilterContext.Provider value={{ filters, setFilters }}>
      {children}
    </SapFilterContext.Provider>
  );
}
